import { useCallback, useEffect } from 'react';

import { useRecoilState } from 'recoil';
import { timestampsState } from 'recoils/common/menuState';

import { getTimeStampsLatest } from 'apis/etcApi';

import {
  getNewUpdateDate,
  isWithin30DaysFrom,
} from 'utils/date/convertDateUtil';
import { getMenuTimestamps } from 'utils/menu/routePageUtil';

const useMenuCommon = () => {
  const [timestamps, setTimestamps] = useRecoilState(timestampsState);

  /**
   * @function fetchTimestamps
   * @desc 타임스탬프 데이터를 API로부터 가져오는 함수
   * - 외부에서 호출 가능하도록 useCallback으로 감싸서 반환
   */
  const fetchTimestamps = useCallback(async () => {
    try {
      const response = await getTimeStampsLatest();
      if (response.success && response.data) {
        // routePageUtil에서 계산된 타임스탬프 맵 가져오기
        const localTimestamps = getMenuTimestamps();

        // 1. 데이터 병합 (API 데이터 + 로컬 가이드 데이터)
        const combinedData = {
          ...response.data,
          ...localTimestamps,
        };

        // 2. 모든 항목에 대해 NEW/UPDATE 및 30일 이내 여부 계산
        const processedTimestamps = Object.keys(combinedData).reduce(
          (acc, key) => {
            const { createdAt, updatedAt } = combinedData[key];

            // 공지사항(notice) 및 프로모션(promotion)의 경우 updatedAt을 항상 null로 처리
            const finalUpdatedAt =
              key === 'notice' || key === 'promotion' ? null : updatedAt;

            const { date, value } = getNewUpdateDate(
              createdAt,
              finalUpdatedAt
            );

            // 30일 이내인 경우만 상태에 포함
            if (isWithin30DaysFrom(date)) {
              acc[key] = {
                ...combinedData[key],
                updatedAt: finalUpdatedAt,
                value,
                date,
              };
            }
            return acc;
          },
          {}
        );

        setTimestamps(processedTimestamps);
      }
    } catch (error) {
      return;
    }
  }, [setTimestamps]);

  /**
   * @function useEffect
   * @desc 뱃지(빨간 점) 표시를 위한 useEffect
   * - 메뉴 항목별로 createdAt 또는 updatedAt 기준으로 '30일 이내'인 경우에만 new 뱃지를 표시함
   * - 상위 메뉴는 자식 항목 중 하나라도 new가 true이면 상위에도 new: true를 부여
   */
  useEffect(() => {
    if (!timestamps) {
      fetchTimestamps();
    }
  }, [timestamps, fetchTimestamps]);

  return {
    refetchTimestamps: fetchTimestamps,
  };
};

export default useMenuCommon;
